import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { Coins, Brain, Lightning, MagnifyingGlass, ShieldCheck, FloppyDisk } from "@phosphor-icons/react";

const usd = (n) => `${(n ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 3 })} $`;
const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - i);
  return { value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) };
});
const PROVIDER_LABEL = { exa: "Exa", prospeo: "Prospeo", coldiq: "ColdIQ", brightdata: "Bright Data", serpapi: "SerpApi (Google Jobs)", hasdata: "HasData", apify: "Apify" };

function Tile({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="surface rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-muted mb-2"><Icon size={14} className="text-accent" />{label}</div>
      <div className="font-display text-2xl font-bold text-fg">{value}</div>
      {sub && <div className="text-[11px] text-muted mt-1">{sub}</div>}
    </div>
  );
}

function Table({ title, rows, testid, labels, credits }) {
  return (
    <div className="surface rounded-lg p-5" data-testid={testid}>
      <div className="overline mb-3">{title}</div>
      {!rows.length && <p className="text-xs text-muted">Aucune dépense sur la période.</p>}
      {rows.map((r) => (
        <div key={r.key} className="flex items-center justify-between text-xs py-1.5 border-b border-border/50" data-testid={`${testid}-${r.key}`}>
          <span className="text-fg truncate pr-2">{(labels || {})[r.key] || r.label || r.key}</span>
          <span className="text-muted shrink-0">{r.calls} appel(s){credits && r.credits ? ` · ${r.credits} crédit(s)` : ""} · <b className="text-fg">{usd(r.cost)}</b></span>
        </div>
      ))}
    </div>
  );
}

function Num({ label, value, onChange, testid, step = 1 }) {
  return (
    <label className="flex items-center justify-between gap-3 text-xs py-1">
      <span className="text-muted">{label}</span>
      <input type="number" step={step} min="0" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="bg-surface-2 border border-border rounded px-2 py-1 w-24 text-right text-fg" />
    </label>
  );
}

function Toggle({ label, checked, onChange, testid, hint }) {
  return (
    <label className="flex items-start gap-2 text-xs py-1 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5" />
      <span><span className="text-fg">{label}</span>{hint && <span className="block text-[10px] text-muted">{hint}</span>}</span>
    </label>
  );
}

function PolicyCard({ data, reload }) {
  const [p, setP] = useState(data.policy);
  const [exaFirst, setExaFirst] = useState(data.exa_first);
  const [emailFb, setEmailFb] = useState(data.email_fallback?.enabled !== false);
  const [budget, setBudget] = useState(data.exa_config?.monthly_budget_usd ?? 100);
  const set = (k) => (v) => setP({ ...p, [k]: typeof p[k] === "boolean" ? v : v === "" ? "" : Number(v) });
  const save = async () => {
    try {
      await api.put("/admin/intel/policy", { policy: p, exa_first: exaFirst, prospeo_email_fallback: emailFb, monthly_budget_usd: Number(budget) });
      toast.success("Politique enregistrée"); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="surface rounded-lg p-5 space-y-3" data-testid="intel-policy-card">
      <div className="overline flex items-center gap-2"><ShieldCheck size={13} className="text-accent" /> Politique Exa-first · Prospeo en secours</div>
      <p className="text-[11px] text-muted">Ordre actuel des décideurs : {(data.waterfall?.person_search || []).join(" → ")}. Exa {data.exa_configured ? "configuré" : "NON configuré"}.</p>
      <Toggle label="Exa en priorité pour les décideurs et les domaines" checked={exaFirst} onChange={setExaFirst} testid="policy-exa-first" hint="Désactivé : retour au waterfall Bright Data → Prospeo → ColdIQ." />
      <Toggle label="Prospeo en secours pour les emails manquants (opportunités)" checked={emailFb} onChange={setEmailFb} testid="policy-email-fallback" />
      <Toggle label="Prospeo en secours pour Lead Intelligence" checked={p.prospeo_fallback} onChange={set("prospeo_fallback")} testid="policy-lead-prospeo" />
      <Toggle label="Recherche approfondie automatique dans les missions" checked={p.mission_research_enabled} onChange={set("mission_research_enabled")} testid="policy-mission-research" />
      <div className="border-t border-border/60 pt-2">
        <Num label="Entreprises analysées par mission (max 8)" value={p.mission_research_targets} onChange={set("mission_research_targets")} testid="policy-mission-targets" />
        <Num label="Coût max. par mission ($)" step={0.01} value={p.max_cost_per_mission_usd} onChange={set("max_cost_per_mission_usd")} testid="policy-mission-max-cost" />
        <Num label="Fiches entreprise à la demande / utilisateur / jour" value={p.user_intel_daily_cap} onChange={set("user_intel_daily_cap")} testid="policy-user-cap" />
        <Num label="Durée de cache d'une recherche (jours)" value={p.research_ttl_days} onChange={set("research_ttl_days")} testid="policy-ttl" />
        <Num label="Lead Intel : emails Prospeo par recherche" value={p.lead_prospeo_max_per_search} onChange={set("lead_prospeo_max_per_search")} testid="policy-lead-per-search" />
        <Num label="Lead Intel : emails Prospeo par jour" value={p.lead_prospeo_daily_cap} onChange={set("lead_prospeo_daily_cap")} testid="policy-lead-daily" />
        <Num label="Budget Exa mensuel ($)" value={budget} onChange={setBudget} testid="policy-exa-budget" />
      </div>
      <button onClick={save} className="w-full bg-accent text-black text-xs font-semibold rounded-md py-2 flex items-center justify-center gap-1.5 hover-lift" data-testid="policy-save-btn"><FloppyDisk size={13} /> Enregistrer la politique</button>
    </div>
  );
}

export default function AdminCosts() {
  const [month, setMonth] = useState(MONTHS[0].value);
  const [r, setR] = useState(null);
  const [pol, setPol] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/costs", { params: { month } }).then(({ data }) => setR(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/admin/intel/policy").then(({ data }) => setPol(data)).catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);
  if (!r || !pol) return <div className="text-muted text-sm animate-pulse">Chargement…</div>;
  const b = r.exa_budget;
  const raw = Math.min(100, (100 * b.spent_usd) / (b.monthly_budget_usd || 1));
  const pct = raw < 10 ? Math.round(raw * 10) / 10 : Math.round(raw);
  const pctLabel = raw > 0 && pct === 0 ? "< 0,1" : pct.toLocaleString("fr-FR");
  const prospeo = r.by_provider.find((x) => x.key === "prospeo");
  return (
    <div className="space-y-5" data-testid="admin-costs-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline !text-accent flex items-center gap-2"><Coins size={14} /> Supervision des coûts</div>
          <h1 className="font-display text-3xl font-bold text-fg mt-1">Coûts & budget</h1>
          <p className="text-sm text-muted mt-1">Dépenses par fournisseur, fonctionnalité, organisation et mission de recherche.</p>
        </div>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="costs-month-input"
          className="bg-surface border border-border rounded-md px-3 py-2 text-sm text-fg capitalize">
          {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Total estimé du mois" value={usd(r.total_usd)} icon={Coins} testid="costs-total" sub={`${r.by_provider.reduce((s, x) => s + x.calls, 0)} appel(s) payant(s)`} />
        <Tile label="Budget Exa consommé" value={`${pctLabel} %`} icon={Brain} testid="costs-exa-budget" sub={`${usd(b.spent_usd)} / ${usd(b.monthly_budget_usd)}${b.exhausted ? " · ÉPUISÉ" : ""}`} />
        <Tile label="Missions de recherche" value={r.missions_count} icon={MagnifyingGlass} testid="costs-missions" sub={`coût moyen ${usd(r.avg_mission_cost)}`} />
        <Tile label="Prospeo (secours)" value={prospeo ? prospeo.calls : 0} icon={Lightning} testid="costs-prospeo" sub={prospeo ? `${usd(prospeo.cost)} estimés` : "aucun appel"} />
      </div>
      <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden" data-testid="costs-exa-gauge"><div className="h-full bg-accent transition-all duration-700" style={{ width: `${pct}%` }} /></div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="surface rounded-lg p-5 lg:col-span-2" data-testid="costs-chart">
          <div className="overline mb-3">Dépenses par jour</div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={r.by_day}>
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#8A8A93" }} />
                <YAxis tick={{ fontSize: 10, fill: "#8A8A93" }} width={44} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272A", fontSize: 12 }} formatter={(v) => usd(v)} />
                <Area dataKey="cost" name="Coût" stroke="#00E5FF" fill="#00E5FF22" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <PolicyCard key={JSON.stringify(pol.policy) + pol.exa_first} data={pol} reload={load} />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Table title="Par fournisseur" rows={r.by_provider} testid="costs-by-provider" labels={PROVIDER_LABEL} credits />
        <Table title="Par fonctionnalité" rows={r.by_feature} testid="costs-by-feature" />
        <Table title="Par opération" rows={r.by_operation} testid="costs-by-operation" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="surface rounded-lg p-5" data-testid="costs-top-orgs">
          <div className="overline mb-3">Organisations les plus consommatrices</div>
          {!r.top_organizations.length && <p className="text-xs text-muted">Aucune dépense attribuée à une organisation.</p>}
          {r.top_organizations.map((o) => <div key={o.org_id} className="flex justify-between text-xs py-1.5 border-b border-border/50"><span className="text-fg">{o.name}</span><span className="text-muted">{o.calls} appel(s) · <b className="text-fg">{usd(o.cost)}</b></span></div>)}
        </div>
        <div className="surface rounded-lg p-5" data-testid="costs-top-missions">
          <div className="overline mb-3">Missions les plus coûteuses</div>
          {!r.top_missions.length && <p className="text-xs text-muted">Aucune mission facturée sur la période.</p>}
          {r.top_missions.map((m) => <div key={m.id} className="flex justify-between gap-3 text-xs py-1.5 border-b border-border/50"><span className="text-fg truncate">{m.query}</span><span className="text-muted shrink-0">{m.researched} fiche(s) · <b className="text-fg">{usd(m.cost)}</b></span></div>)}
        </div>
      </div>
      <ul className="text-[11px] text-muted space-y-0.5">{r.notes.map((n) => <li key={n}>• {n}</li>)}</ul>
    </div>
  );
}
